// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { Box } from '@mui/material';
import type { ButtonProps } from '@mui/material/Button';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  InstrumentAllowance,
  TransferPreapproval,
} from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Model/TransferPreapproval';
import { ContractId } from '@daml/types';
import { AsyncButton } from '../../../components/Button/AsyncButton';
import PartyDisplay from '../../../components/PartyDisplay';

export type TransferPreapprovalRow = {
  contractId: ContractId<TransferPreapproval>;
  payload: TransferPreapproval;
  state: string;
  reason?: string;
};

export type PreapprovalAction = {
  name: string;
  color: ButtonProps['color'];
  action: (row: TransferPreapprovalRow) => Promise<unknown>;
};

// Helper functions for allowance formatting
const sortAllowancesByIdAsc = (allowances: InstrumentAllowance[]) =>
  [...allowances].sort((a, b) => a.id.localeCompare(b.id));

const getAllowances = (payload: TransferPreapproval) => payload.instrumentAllowances ?? [];

const hasAllowances = (allowances: InstrumentAllowance[]) =>
  Array.isArray(allowances) && allowances.length > 0;

export const createTransferPreapprovalColumns = (
  transferPreapprovals: TransferPreapprovalRow[],
  preapprovalActions: (row: TransferPreapprovalRow, onModify: () => void) => PreapprovalAction[],
  setModifyPreapprovalContract: (contract: TransferPreapprovalRow) => void,
): GridColDef<TransferPreapprovalRow>[] => [
  {
    field: 'instrumentAdmin',
    headerName: 'Instrument Admin',
    flex: 1,
    valueGetter: (params: GridRenderCellParams<TransferPreapprovalRow>) =>
      params.row.payload.instrumentAdmin,
    renderCell: (params: GridRenderCellParams<TransferPreapprovalRow>) => (
      <PartyDisplay party={params.row.payload.instrumentAdmin} />
    ),
  },
  {
    field: 'instrumentAllowances',
    headerName: 'Instrument IDs',
    flex: 1,
    valueGetter: (params: GridRenderCellParams<TransferPreapprovalRow>) => {
      const allowances = getAllowances(params.row.payload);
      if (!hasAllowances(allowances)) {
        return 'All instruments allowed';
      }
      return sortAllowancesByIdAsc(allowances)
        .map((a) => a.id)
        .join('\n');
    },
    renderCell: (params: GridRenderCellParams<TransferPreapprovalRow>) => {
      const allowances = getAllowances(params.row.payload);
      if (!hasAllowances(allowances)) {
        return <span>All instruments allowed</span>;
      }
      return (
        <Box sx={{ whiteSpace: 'normal', lineHeight: 1.5, py: 0, my: 0 }}>
          {sortAllowancesByIdAsc(allowances).map((a) => (
            <div key={a.id} style={{ lineHeight: 1.5, padding: 0, margin: 0 }}>
              {a.id}
            </div>
          ))}
        </Box>
      );
    },
  },
  {
    field: 'actions',
    headerName: 'Actions',
    minWidth: 200,
    sortable: false,
    filterable: false,
    renderCell: (params: GridRenderCellParams<TransferPreapprovalRow>) => {
      const contract = transferPreapprovals.find((c) => c.contractId === params.row.contractId);
      if (!contract) return null;
      const { row } = params;
      const actions = preapprovalActions(row, () => setModifyPreapprovalContract(contract));
      return (
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
          {actions.map((action) => (
            <AsyncButton
              key={action.name}
              color={action.color}
              variant="contained"
              size="small"
              sx={{ minWidth: 80 }}
              onClick={() => action.action(row)}
            >
              {action.name}
            </AsyncButton>
          ))}
        </Box>
      );
    },
  },
];
